import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';

/**
 * 메인 검색창.
 *   - 엔터/버튼 → /analyze/:keyword 로 이동 (onSearch override 가능)
 *   - 자동완성: 입력 변경 시 150ms 디바운스 후 /api/suggest?q= 호출 (prop suggestions 가 주어지면 그대로 사용)
 *   - 크기: hero(랜딩) / md(대시보드)
 */
export function SearchBar({
  initial = '',
  size = 'md',
  placeholder = '키워드를 입력하세요 (예: 다이어트 보조제)',
  onSearch,
  suggestions: externalSuggestions,
  onChange,
  className,
  autoFocus,
  autocomplete = true,
}: {
  initial?: string;
  size?: 'md' | 'hero';
  placeholder?: string;
  onSearch?: (kw: string) => void;
  /** 부모가 직접 자동완성 결과를 주입하고 싶을 때 (생략 시 내부에서 fetch) */
  suggestions?: string[];
  onChange?: (v: string) => void;
  className?: string;
  autoFocus?: boolean;
  autocomplete?: boolean;
}) {
  const navigate = useNavigate();
  const [value, setValue] = useState(initial);
  const [focused, setFocused] = useState(false);
  const [autoList, setAutoList] = useState<string[]>([]);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setFocused(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // 디바운스 자동완성
  useEffect(() => {
    if (!autocomplete || externalSuggestions) return;
    const v = value.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!v) {
      setAutoList([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      api.keyword
        .suggest(v)
        .then((res) => setAutoList(res.suggestions))
        .catch(() => setAutoList([]));
    }, 150);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, autocomplete, externalSuggestions]);

  function submit(kw: string = value) {
    const t = kw.trim();
    if (!t) return;
    if (onSearch) onSearch(t);
    else navigate(`/analyze/${encodeURIComponent(t)}`);
  }

  const isHero = size === 'hero';
  const suggestions = externalSuggestions ?? autoList;

  return (
    <div ref={wrapperRef} className={cn('relative w-full', className)}>
      <form
        className={cn(
          'flex items-center gap-2 rounded-2xl bg-gray-900/80 border transition-all backdrop-blur',
          focused ? 'border-gold shadow-[0_0_0_4px_rgba(255,215,0,0.15)]' : 'border-gray-800',
          isHero ? 'h-16 px-4' : 'h-12 px-3',
        )}
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Search
          className={cn('shrink-0 text-gold', isHero ? 'h-6 w-6' : 'h-5 w-5')}
          aria-hidden="true"
        />
        <input
          autoFocus={autoFocus}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            onChange?.(e.target.value);
          }}
          onFocus={() => setFocused(true)}
          placeholder={placeholder}
          className={cn(
            'flex-1 bg-transparent outline-none placeholder:text-gray-500 text-white',
            isHero ? 'text-lg' : 'text-sm',
          )}
        />
        <Button
          type="submit"
          size={isHero ? 'lg' : 'md'}
          variant="gold"
          className="shrink-0"
        >
          분석하기
        </Button>
      </form>

      {focused && suggestions.length > 0 && (
        <ul className="absolute z-30 mt-2 w-full overflow-hidden rounded-xl border border-gray-800 bg-gray-900 shadow-2xl">
          {suggestions.slice(0, 8).map((s) => (
            <li
              key={s}
              onMouseDown={(e) => {
                e.preventDefault();
                setValue(s);
                submit(s);
              }}
              className="cursor-pointer px-4 py-2.5 text-sm text-gray-200 hover:bg-gray-800"
            >
              <Search className="mr-2 inline h-3.5 w-3.5 text-gold" />
              {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
